import { html, LitElement } from "lit";
import { customElement, property, queryAssignedElements, state } from 'lit/decorators.js';
import { styleMap } from "lit/directives/style-map.js";
import { when } from "lit/directives/when.js";
import { localized, msg, str } from '@lit/localize';
import '../icon';
import style from './usos-frame.scss';
import { UsosFrameSection } from "./usos-frame-section";


/**
 * Frame for content with header and optional footer.
 *
 * @slot - frame content
 * @slot title - title of frame
 * @slot trailing-content - extra content to put on the right side of the header
 * @slot foot - content for footer
 */
@customElement('usos-frame')
@localized()
export class UsosFrame extends LitElement {
    static override styles = style;

    @state() hasHeader = false;

    /**
     * URL to leading icon.
     */
    @property({reflect: true, attribute: 'leading-icon-url'}) leadingIconUrl: string | null = null;

    /**
     * Leading icon name, {@see UsosIcon.iconName}.
     */
    @property({attribute: 'leading-icon'}) leadingIcon: string | null = null;

    /**
     * Should the component be expadable
     */
    @property({type: Boolean}) expandable = false;

    /**
     * If usos-frame is expandable, then this attribute indicates whether it is expanded or not.
     */
    @property({type: Boolean, reflect: true}) expanded = false;

    /** Should the footer be sticky? */
    @property({type: Boolean, reflect: true, attribute: 'footer-sticky'}) footerSticky = false;

    /** Should frame width fit content? */
    @property({type: Boolean, reflect: true, attribute: 'fit-content'}) fitContent = false;

    @queryAssignedElements({slot: 'title'})
    private _titleElements!: HTMLElement[];

    @queryAssignedElements()
    private _frameBodyElements!: HTMLElement[];

    override render() {
        const headerStyle = {display: !this.hasHeader ? 'none' : null};
        const leadingIconStyle = {display: (this.leadingIconUrl == null && this.leadingIcon == null) ? 'none' : null};
        return html`
            <div id="header" style=${styleMap(headerStyle)}>
                <usos-icon id="leading-icon" .iconUrl=${this.leadingIconUrl} .iconName=${this.leadingIcon} style=${styleMap(leadingIconStyle)}></usos-icon>
                <div id="title"><slot name="title" @slotchange=${this._onTitleSlotChange}></slot></div>
                <div id="trailing-content"><slot name="trailing-content"></slot></div>
                ${when(this.expandable, () => html`
                    <usos-icon id="expand-button" is-button .iconName=${this.expanded ? 'expand_less' : 'expand_more'}
                        .label=${this.expanded ? msg(str`zwiń`) : msg(str`rozwiń`)}
                        aria-controls="body foot" aria-expanded=${this.expanded}
                        @click=${this._toggleExpand}></usos-icon>
                `)}
            </div>
            <div id="body">
                <slot @slotchange=${this._propagateHeaderLevel}></slot>
            </div>
            <div id="foot"><slot name="foot"></slot></div>
        `;
    }

    private _toggleExpand() {
        this.expanded = !this.expanded;
    }

    private _onTitleSlotChange(e: Event) {
        this.hasHeader = (e.target as HTMLSlotElement).assignedElements().length > 0;
        this._propagateHeaderLevel();
    }

    private _propagateHeaderLevel() {
        if (this.hasHeader) {
            const sections = this._frameBodyElements.filter(el => el instanceof UsosFrameSection);
            const title = this._titleElements[0];
            let level: Optional<number> = null;
            if (title instanceof HTMLHeadingElement) {
                level = Number(title.tagName[1]);
            } else if (title.role == 'heading' && title.ariaLevel != null) {
                level = Number(title.ariaLevel);
            }
            if (level != null) {
                sections.forEach(el => el.level = level);
            }
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
      "usos-frame": UsosFrame,
    }
}
